// Modal para asignar platos a un menu en matriz dieta × tiempo × dia_semana.
// La nutricionista elige una dieta + dia y marca el plato para cada tiempo.
// El payload final agrupa todas las celdas marcadas y las envia al endpoint.
import { useMemo, useState } from 'react'
import type {
  AsignarPlatoItem,
  DietaResponse,
  MenuResponse,
  PlatoTemplateResponse,
} from '../../services/nutricionista'
import { ModalShell } from '../ui/ModalShell'

interface AsignarPlatosModalProps {
  menu: MenuResponse
  dietas: DietaResponse[]
  platos: PlatoTemplateResponse[]
  onConfirm: (items: AsignarPlatoItem[]) => Promise<void> | void
  onCancel: () => void
}

// Fallback estatico para tiempos cuando el backend no expone /tiempos
// publicamente (T11 frontend no consume catalogo de tiempos directo).
// Estos ids deben coincidir con los seeds de Sprint 1.
const TIEMPOS_DEFAULT: { id: number; codigo: string; nombre: string }[] = [
  { id: 1, codigo: 'DESAYUNO', nombre: 'Desayuno' },
  { id: 2, codigo: 'REFA_AM',  nombre: 'Refaccion AM' },
  { id: 3, codigo: 'ALMUERZO', nombre: 'Almuerzo' },
  { id: 4, codigo: 'REFA_PM',  nombre: 'Refaccion PM' },
  { id: 5, codigo: 'CENA',     nombre: 'Cena' },
]

const DIAS = [
  { id: 1, nombre: 'Lun' },
  { id: 2, nombre: 'Mar' },
  { id: 3, nombre: 'Mie' },
  { id: 4, nombre: 'Jue' },
  { id: 5, nombre: 'Vie' },
  { id: 6, nombre: 'Sab' },
  { id: 7, nombre: 'Dom' },
]

type CellKey = string // `${dieta_id}|${tiempo_id}|${dia_semana}`

function cellKey(dieta_id: number, tiempo_id: number, dia: number): CellKey {
  return `${dieta_id}|${tiempo_id}|${dia}`
}

export default function AsignarPlatosModal({
  menu,
  dietas,
  platos,
  onConfirm,
  onCancel,
}: AsignarPlatosModalProps) {
  const [dietaSel, setDietaSel] = useState<number | ''>(dietas[0]?.id ?? '')
  const [diaSel, setDiaSel] = useState<number>(1)
  // Mapa { celda -> plato_id }
  const [asignaciones, setAsignaciones] = useState<Map<CellKey, number>>(new Map())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const platosAprobados = useMemo(
    () =>
      platos.filter(
        (p) =>
          String(p.estado).toUpperCase() === 'APROBADO' ||
          String(p.estado).toUpperCase() === 'VERIFICADO_CHEF',
      ),
    [platos],
  )

  function setPlatoCelda(tiempo_id: number, plato_id: number | '') {
    if (dietaSel === '') return
    const key = cellKey(Number(dietaSel), tiempo_id, diaSel)
    setAsignaciones((prev) => {
      const next = new Map(prev)
      if (plato_id === '') next.delete(key)
      else next.set(key, plato_id)
      return next
    })
  }

  const items: AsignarPlatoItem[] = useMemo(() => {
    return Array.from(asignaciones.entries()).map(([k, plato_id]) => {
      const [d, t, day] = k.split('|').map(Number)
      return {
        dieta_id: d,
        tiempo_id: t,
        dia_semana: day,
        plato_id,
        orden_plato: 1,
      }
    })
  }, [asignaciones])

  async function handleConfirm() {
    setError(null)
    if (items.length === 0) {
      setError('Asigna al menos una celda antes de confirmar.')
      return
    }
    setSubmitting(true)
    try {
      await onConfirm(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al asignar')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell
      title={`Asignar platos — ${menu.codigo} ${menu.nombre}`}
      subtitle="Matriz dieta × tiempo × dia_semana. Selecciona dieta + dia, asigna plato por cada tiempo. Al confirmar se envian todas las celdas marcadas."
      onClose={onCancel}
      closeDisabled={submitting}
      maxWidth={640}
    >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <label className="block">
            <span className="text-sm font-semibold">Dieta</span>
            <select
              value={dietaSel}
              onChange={(e) => setDietaSel(e.target.value === '' ? '' : Number(e.target.value))}
              aria-label="Dieta"
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
            >
              {dietas.map((d) => (
                <option key={d.id} value={d.id}>{d.codigo} — {d.nombre}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-semibold">Dia</span>
            <select
              value={diaSel}
              onChange={(e) => setDiaSel(Number(e.target.value))}
              aria-label="Dia de la semana"
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
            >
              {DIAS.map((d) => (
                <option key={d.id} value={d.id}>{d.nombre}</option>
              ))}
            </select>
          </label>
        </div>

        <table className="w-full text-sm border" aria-label="Matriz dieta tiempo plato">
          <thead className="bg-gray-100 dark:bg-gray-700 text-left">
            <tr>
              <th className="p-2">Tiempo</th>
              <th className="p-2">Plato asignado</th>
            </tr>
          </thead>
          <tbody>
            {TIEMPOS_DEFAULT.map((t) => {
              const key = dietaSel === '' ? '' : cellKey(Number(dietaSel), t.id, diaSel)
              const plato_id = key ? asignaciones.get(key) ?? '' : ''
              return (
                <tr key={t.id} className="border-t">
                  <td className="p-2 font-semibold">{t.nombre}</td>
                  <td className="p-2">
                    <select
                      value={plato_id}
                      onChange={(e) =>
                        setPlatoCelda(
                          t.id,
                          e.target.value === '' ? '' : Number(e.target.value),
                        )
                      }
                      aria-label={`Plato para ${t.nombre}`}
                      className="w-full border rounded px-1 py-0.5 text-sm"
                    >
                      <option value="">— sin asignar —</option>
                      {platosAprobados.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.codigo} — {p.nombre_compuesto}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <p className="text-xs text-gray-500 mt-2" data-testid="celdas-totales">
          Celdas totales asignadas en este menu: {items.length}
        </p>

        {error && (
          <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2">
            {error}
          </p>
        )}

        <div className="flex gap-2 pt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 border py-2 rounded text-sm disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || items.length === 0}
            className="flex-1 bg-avante-primary text-white py-2 rounded text-sm font-semibold disabled:opacity-50"
          >
            {submitting ? 'Asignando…' : `Confirmar (${items.length})`}
          </button>
        </div>
    </ModalShell>
  )
}
